import React, { useEffect, useState } from "react";
import apiClient from "../../Api/apiConfig";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Edit, Trash2, UserCog, X } from "lucide-react";
import { useAuth } from "../../lib/auth";
import RegisterPage from "../../pages/RegisterPage";
import "../../styles/MembersTable.css";

const columnHelper = createColumnHelper();

// Helper pour récupérer le token où qu'il soit
const getAuthToken = () =>
  localStorage.getItem("authToken") ||
  sessionStorage.getItem("authToken");

export default function MembersTable() {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const { user, isAdmin } = useAuth();

  // États pour le modal d'édition
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    role: "user",
  });

  // État pour le modal d'inscription
  const [registrationModalOpen, setRegistrationModalOpen] = useState(false);

  // 🔹 Mise à jour du formulaire
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // 🔹 Récupérer les membres
  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const token = getAuthToken();
      if (!token) {
        setError("Vous n'êtes pas authentifié.");
        setIsLoading(false);
        return;
      }

      const response = await apiClient.get("/users", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status !== 200) {
        throw new Error("Erreur lors de la récupération des membres");
      }

      // On suppose que response.data est un tableau
      setData(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error("❌ Erreur lors de la récupération des membres :", err);
      setError(
        "Impossible de charger les membres. Veuillez réessayer plus tard."
      );
    } finally {
      setIsLoading(false);
    }
  };

  // 🔹 Ouvrir/Fermer le modal d'inscription
  const openRegistrationModal = () => setRegistrationModalOpen(true);
  const closeRegistrationModal = () => setRegistrationModalOpen(false);

  // 🔹 Supprimer un membre
  const handleDelete = async (id) => {
    if (!id) {
      console.error("❌ ID manquant pour la suppression.");
      alert("Erreur : ID du membre introuvable.");
      return;
    }
    if (!window.confirm("Voulez-vous vraiment supprimer ce membre ?")) return;

    console.log(`🗑 Suppression de l'utilisateur avec ID: ${id}`);

    try {
      const token = getAuthToken();
      if (!token) {
        alert("Vous n'êtes pas authentifié.");
        return;
      }

      const response = await apiClient.delete(`/users/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status !== 200) {
        throw new Error("Erreur lors de la suppression du membre");
      }

      setData((prev) => prev.filter((member) => member.id !== id));
      console.log(`✅ Utilisateur ID: ${id} supprimé.`);
    } catch (err) {
      console.error("❌ Erreur lors de la suppression :", err);
      alert("Une erreur s'est produite lors de la suppression.");
    }
  };

  // 🔹 Ouvrir le modal d'édition
  const openModal = (userObj = null) => {
    setEditingUser(userObj);
    setFormData(
      userObj || { first_name: "", last_name: "", email: "", role: "user" }
    );
    setModalOpen(true);
  };

  // 🔹 Fermer le modal d'édition
  const closeModal = () => {
    setModalOpen(false);
    setEditingUser(null);
    setFormData({ first_name: "", last_name: "", email: "", role: "user" });
  };

  // 🔹 Colonnes de la table
  const columns = [
    columnHelper.accessor("first_name", { header: "Prénom" }),
    columnHelper.accessor("last_name", { header: "Nom" }),
    columnHelper.accessor("email", { header: "Email" }),
    columnHelper.accessor("role", { header: "Rôle" }),
    columnHelper.accessor("id", {
      header: "Actions",
      cell: (info) => (
        <div className="flex space-x-2">
          {isAdmin && (
            <>
              <button
                onClick={() => openModal(info.row.original)}
                className="text-blue-600 hover:text-blue-800"
                title="Modifier le membre"
              >
                <Edit />
              </button>
              <button
                onClick={() => handleDelete(info.row.original.id)}
                className="text-red-600 hover:text-red-800"
                title="Supprimer le membre"
              >
                <Trash2 />
              </button>
            </>
          )}
        </div>
      ),
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  if (isLoading) return <p>Chargement des membres...</p>;
  if (error) return <p className="text-red-600">{error}</p>;

  return (
    <div className="mt-8 flex flex-col bg-white shadow rounded-lg">
      <div className="p-4 border-b flex justify-between items-center">
        <h2 className="text-lg font-medium">Gestion des membres</h2>
        {isAdmin && (
          <button
            onClick={openRegistrationModal}
            className="bg-blue-600 text-white px-4 py-2 rounded-md"
          >
            <UserCog className="h-5 w-5 inline-block mr-2" />
            Ajouter un membre
          </button>
        )}
      </div>

      <div className="overflow-x-auto">
        {data.length === 0 ? (
          <div className="p-4 text-sm text-gray-500">
            Aucun membre trouvé pour le moment.
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-6 py-3 text-left text-sm font-semibold text-gray-900"
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-gray-200">
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="px-6 py-4 text-sm text-gray-500"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal d'édition */}
      {modalOpen && (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal-content">
            <button className="modal-close" onClick={closeModal}>
              <X />
            </button>
            <h2>Modifier le membre</h2>
            <div className="grid grid-cols-1 gap-4 mt-4">
              <input
                type="text"
                name="first_name"
                placeholder="Prénom"
                value={formData.first_name}
                onChange={handleFormChange}
                autoFocus
                className="border p-2 rounded"
              />
              <input
                type="text"
                name="last_name"
                placeholder="Nom"
                value={formData.last_name}
                onChange={handleFormChange}
                className="border p-2 rounded"
              />
              <input
                type="email"
                name="email"
                placeholder="Email"
                value={formData.email}
                onChange={handleFormChange}
                className="border p-2 rounded"
              />
              <input
                type="text"
                name="role"
                placeholder="Rôle"
                value={formData.role}
                onChange={handleFormChange}
                className="border p-2 rounded"
              />
            </div>
            <div className="mt-4">
              <button
                onClick={() => {
                  const token = getAuthToken();
                  if (!token) {
                    alert("Vous n'êtes pas authentifié.");
                    return;
                  }
                  apiClient
                    .put(`/users/${editingUser.id}`, formData, {
                      headers: {
                        Authorization: `Bearer ${token}`,
                      },
                    })
                    .then(() => {
                      closeModal();
                      fetchUsers();
                    })
                    .catch((err) => {
                      console.error("Erreur lors de la mise à jour :", err);
                      alert(
                        "Une erreur s'est produite lors de la mise à jour."
                      );
                    });
                }}
                className="bg-blue-600 text-white px-4 py-2 rounded-md"
              >
                Enregistrer
              </button>
              <button
                onClick={closeModal}
                className="bg-gray-300 text-black px-4 py-2 rounded-md ml-2"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'inscription */}
      {registrationModalOpen && (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal-content">
            <button className="modal-close" onClick={closeRegistrationModal}>
              <X />
            </button>
            <RegisterPage closeModal={closeRegistrationModal} />
          </div>
        </div>
      )}
    </div>
  );
}
